// Sums monthly premium across a client's active (non-waived) employee
// enrollments. Computation now lives server-side in
// `GET /api/clients/:id/monthly-premium` (one request) — previously this hook
// fan-out N+1 fetches per enrollment from the browser.

import { useQuery } from "@tanstack/react-query";

export function useClientMonthlyPremium(
  clientId: number | undefined,
): { total: number; isLoading: boolean } {
  const { data = 0, isLoading } = useQuery<number>({
    queryKey: ["/metrics/client-monthly-premium", clientId],
    enabled: !!clientId,
    queryFn: async () => {
      const res = await fetch(`/api/clients/${clientId}/monthly-premium`);
      if (!res.ok) return 0;
      const { total } = await res.json();
      return Number(total) || 0;
    },
  });

  // `isLoading` is only true on the *first* fetch (no cached data yet) — the
  // right signal for showing a skeleton in place of the value. We deliberately
  // don't surface `isFetching` so background revalidations don't flash the UI.
  return { total: data, isLoading: !!clientId && isLoading };
}
